import * as am4core from "@amcharts/amcharts4/core";
import type { BaseObject } from "@amcharts/amcharts4/.internal/core/Base";
import type { ITheme } from "@amcharts/amcharts4/.internal/themes/ITheme";

/**
 * The theme target is an amCharts {@link BaseObject} whose concrete subclass
 * (and therefore its available properties) is selected at runtime via
 * `className`. amCharts exposes no single static type covering every branch
 * below, so this interface describes the union of members this theme touches;
 * each is read only inside the matching `className` guard.
 */
interface ChartThemeTarget extends BaseObject {
  fill?: am4core.Color;
  fontWeight?: number;
  slices?: { template: { stroke: am4core.Color } };
  columns?: { template: { stroke: am4core.Color; fillOpacity: number } };
  segments?: { template: am4core.Sprite };
  bulletsContainer?: am4core.Sprite;
  circle?: { fill: am4core.Color };
  tooltip?: am4core.Tooltip;
  grid?: { template: { stroke: am4core.Color } };
  labels?: { template: { tooltip: am4core.Tooltip } };
}

export const DarkChartTheme: ITheme = (object: BaseObject) => {
  const target = object as ChartThemeTarget;

  if (target.className === "Label") {
    target.fill = am4core.color("#cac9d7");
  }

  if (target.className === "PieSeries") {
    target.slices!.template.stroke = am4core.color("#27272c");
  }

  if (target.className === "ColumnSeries") {
    target.columns!.template.stroke = am4core.color("#27272c");
    target.columns!.template.fillOpacity = 0.9;
  }

  if (target.className === "LineSeries") {
    let segment = target.segments!.template;

    let hoverState = segment.states.create("hover");
    hoverState.properties.strokeWidth = 3;

    let dimmed = segment.states.create("dimmed");
    dimmed.properties.stroke = am4core.color("#41425a");

    const bulletDimmed = target.bulletsContainer!.states.create("dimmed");
    bulletDimmed.properties.stroke = am4core.color("#41425a");
  }

  if (target.className === "CircleBullet") {
    target.circle!.fill = am4core.color("#27272c");
  }

  if (target.className === "DateAxis" || target.className === "ValueAxis") {
    const tooltip = target.tooltip;
    if (!!tooltip) {
      tooltip.background.fill = am4core.color("#41425a");
      tooltip.background.stroke = am4core.color("#cac9d7");
    }
  }

  if (
    target.className === "AxisRendererX" ||
    target.className === "AxisRendererY"
  ) {
    target.fill = am4core.color("#cac9d7");
    target.fontWeight = 400;
    target.grid!.template.stroke = am4core.color("#cac9d7");

    let labelTooltip = new am4core.Tooltip();
    labelTooltip.background.fill = am4core.color("#41425a");
    labelTooltip.background.stroke = am4core.color("#cac9d7");
    labelTooltip.pointerOrientation = "left";
    labelTooltip.getFillFromObject = false;
    labelTooltip.label.wrap = true;

    target.labels!.template.tooltip = labelTooltip;
  }
};
